<script setup>
import { computed } from 'vue';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';

import { Bar, Doughnut } from 'vue-chartjs';
import {
  Chart as ChartJS,
  Title,
  Tooltip,
  Legend,
  BarElement,
  ArcElement,
  CategoryScale,
  LinearScale,
} from 'chart.js';

ChartJS.register(
  Title,
  Tooltip,
  Legend,
  BarElement,
  ArcElement,
  CategoryScale,
  LinearScale
);

import WootDatePicker from 'dashboard/components/ui/DatePicker/DatePicker.vue';
import IspMetricCard from './components/IspMetricCard.vue';
import IspBarChart from './components/IspBarChart.vue';
import IspDataTable from './components/IspDataTable.vue';

import { useIspReport } from './composables/useIspReport';
import { exportCsv } from './composables/useIspExport';
import { getUserRole } from 'dashboard/helper/permissionsHelper';
import './styles/isp-filters.css';

const { t } = useI18n();
const store = useStore();

const {
  data: campanhas,
  retry,
  customDateRange,
  selectedDateRange,
  onDateRangeChange,
  onFilterChange,
  filters,
} = useIspReport({
  getter: 'ispReports/getCampanhas',
  action: 'ispReports/fetchCampanhas',
  filters: { teamId: '' },
  initActions: ['teams/get'],
});

// Team filter (admin/manager only)
const teams = computed(() => store.getters['teams/getTeams'] || []);
const currentUser = computed(() => store.getters.getCurrentUser);
const currentAccountId = computed(() => store.getters.getCurrentAccountId);
const showTeamFilter = computed(() =>
  ['administrator', 'manager'].includes(
    getUserRole(currentUser.value, currentAccountId.value)
  )
);

// Summary data
const summary = computed(() => campanhas.value?.data?.summary || {});
const evolucao = computed(() => campanhas.value?.data?.evolucao || []);
const statusDist = computed(
  () => campanhas.value?.data?.status_distribution || {}
);
const topErrors = computed(() => campanhas.value?.data?.top_errors || []);
const porEquipe = computed(() => campanhas.value?.data?.por_equipe || []);
const tabela = computed(() => campanhas.value?.data?.tabela || []);

// KPI trend helpers
const makeTrend = (val, suffix = '%') => {
  if (val === undefined || val === null || val === 0) return '';
  const sign = val > 0 ? '+' : '';
  return `${sign}${val}${suffix} ${t('ISP_REPORTS.ISP_CAMPANHAS.TREND_VS')}`;
};
const trendDir = val => {
  if (!val) return 'neutral';
  return val > 0 ? 'up' : 'down';
};
// For delivery rate: positive = better = up (green)
const deliveryTrendDir = computed(() => {
  const val = summary.value.delivery_rate_trend;
  if (!val) return 'neutral';
  return val > 0 ? 'up' : 'down';
});
// For failures: positive = worse = down (red)
const failedTrendDir = computed(() => {
  const val = summary.value.total_failed_trend;
  if (!val) return 'neutral';
  return val > 0 ? 'down' : 'up';
});

// ── Chart.js shared config ──
const gridColor = 'rgba(148, 163, 184, 0.06)';
const tickFont = { family: 'Inter, sans-serif', size: 10 };
const tickColor = '#64748b';
const tooltipBase = {
  backgroundColor: 'rgba(15, 23, 42, 0.95)',
  titleFont: { family: 'Inter, sans-serif', size: 11, weight: '600' },
  bodyFont: { family: 'Inter, sans-serif', size: 12 },
  titleColor: '#94a3b8',
  bodyColor: '#e2e8f0',
  borderColor: 'rgba(255,255,255,0.08)',
  borderWidth: 1,
  padding: { top: 8, bottom: 8, left: 12, right: 12 },
  cornerRadius: 8,
  usePointStyle: true,
};
const legendBase = {
  position: 'top',
  align: 'end',
  labels: {
    usePointStyle: true,
    pointStyle: 'circle',
    boxWidth: 6,
    boxHeight: 6,
    padding: 14,
    font: { family: 'Inter, sans-serif', size: 11, weight: '500' },
    color: '#94a3b8',
  },
};

// ── Chart 1: Evolution (grouped bars — sent vs failed per day) ──
const evolucaoLabels = computed(() =>
  evolucao.value.map(item => {
    const d = new Date(item.date + 'T00:00:00');
    return d.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' });
  })
);

const evolucaoBarData = computed(() => ({
  labels: evolucaoLabels.value,
  datasets: [
    {
      label: t('ISP_REPORTS.ISP_CAMPANHAS.SERIE_SENT'),
      data: evolucao.value.map(i => i.sent),
      backgroundColor: 'rgba(93, 173, 226, 0.7)',
      borderColor: '#5DADE2',
      borderWidth: 1,
      borderRadius: 3,
      barPercentage: 0.7,
      categoryPercentage: 0.8,
    },
    {
      label: t('ISP_REPORTS.ISP_CAMPANHAS.SERIE_FAILED'),
      data: evolucao.value.map(i => i.failed),
      backgroundColor: 'rgba(239, 68, 68, 0.7)',
      borderColor: '#ef4444',
      borderWidth: 1,
      borderRadius: 3,
      barPercentage: 0.7,
      categoryPercentage: 0.8,
    },
  ],
}));

const evolucaoBarOptions = computed(() => ({
  responsive: true,
  maintainAspectRatio: false,
  interaction: { mode: 'index', intersect: false },
  plugins: { legend: legendBase, tooltip: tooltipBase },
  scales: {
    x: {
      grid: { color: gridColor, drawBorder: false },
      ticks: { font: tickFont, color: tickColor, padding: 6, maxRotation: 0 },
      border: { display: false },
    },
    y: {
      beginAtZero: true,
      grid: { color: gridColor, drawBorder: false },
      ticks: { font: tickFont, color: tickColor, padding: 8, precision: 0 },
      border: { display: false },
    },
  },
  animation: { duration: 600, easing: 'easeOutQuart' },
}));

// ── Chart 2: Status distribution (doughnut) ──
const statusColors = {
  success: '#10B981',
  partial: '#F59E0B',
  failed: '#EF4444',
  scheduled: '#64748b',
  active: '#5DADE2',
  disabled: '#94a3b8',
};

const statusLabels = {
  success: 'Sucesso',
  partial: 'Parcial',
  failed: 'Falhou',
  scheduled: 'Agendada',
  active: 'Ativa',
  disabled: 'Desativada',
};

const doughnutData = computed(() => {
  const entries = Object.entries(statusDist.value).filter(
    ([, count]) => count > 0
  );
  return {
    labels: entries.map(([key]) => statusLabels[key] || key),
    datasets: [
      {
        data: entries.map(([, count]) => count),
        backgroundColor: entries.map(([key]) => statusColors[key] || '#94a3b8'),
        borderWidth: 0,
        hoverOffset: 4,
      },
    ],
  };
});

const doughnutOptions = computed(() => ({
  responsive: true,
  maintainAspectRatio: false,
  cutout: '65%',
  plugins: {
    legend: {
      position: 'right',
      labels: {
        usePointStyle: true,
        pointStyle: 'circle',
        boxWidth: 8,
        boxHeight: 8,
        padding: 12,
        font: { family: 'Inter, sans-serif', size: 11, weight: '500' },
        color: '#94a3b8',
      },
    },
    tooltip: tooltipBase,
  },
  animation: { duration: 600, easing: 'easeOutQuart' },
}));

// ── Chart 3: Top errors (horizontal bars via IspBarChart) ──
const errorItems = computed(() => {
  const items = topErrors.value;
  if (!items.length) return [];
  const maxCount = Math.max(...items.map(e => e.count)) || 1;
  return items.map(e => ({
    label: e.code,
    value: e.count,
    displayValue: String(e.count),
    percentage: Math.round((e.count / maxCount) * 100),
    color: 'error',
  }));
});

// ── Chart 4: Per-team (horizontal bars via IspBarChart) ──
const teamItems = computed(() => {
  const data = porEquipe.value;
  if (!data.length) return [];
  const maxTotal = Math.max(...data.map(item => item.total)) || 1;
  return data.map(item => ({
    label: item.team,
    value: item.sent,
    displayValue: `${item.sent} / ${item.total}`,
    percentage: Math.round((item.total / maxTotal) * 100),
    color: 'blue',
    subtitle: `${item.failed} ${t('ISP_REPORTS.ISP_CAMPANHAS.LABEL_FAILURES')}`,
  }));
});

// ── Comparison table ──
const tableColumns = [
  {
    key: 'title',
    label: t('ISP_REPORTS.ISP_CAMPANHAS.TABLE_CAMPAIGN'),
    width: '22%',
  },
  {
    key: 'date',
    label: t('ISP_REPORTS.ISP_CAMPANHAS.TABLE_DATE'),
    width: '12%',
  },
  {
    key: 'template',
    label: t('ISP_REPORTS.ISP_CAMPANHAS.TABLE_TEMPLATE'),
    width: '14%',
  },
  {
    key: 'team',
    label: t('ISP_REPORTS.ISP_CAMPANHAS.TABLE_TEAM'),
    width: '12%',
  },
  {
    key: 'total',
    label: t('ISP_REPORTS.ISP_CAMPANHAS.TABLE_TOTAL'),
    align: 'right',
    width: '8%',
  },
  {
    key: 'sent',
    label: t('ISP_REPORTS.ISP_CAMPANHAS.TABLE_SENT'),
    align: 'right',
    width: '8%',
  },
  {
    key: 'failed',
    label: t('ISP_REPORTS.ISP_CAMPANHAS.TABLE_FAILED'),
    align: 'right',
    width: '8%',
  },
  {
    key: 'rate',
    label: t('ISP_REPORTS.ISP_CAMPANHAS.TABLE_RATE'),
    align: 'right',
    type: 'badge',
    width: '8%',
  },
  {
    key: 'duration',
    label: t('ISP_REPORTS.ISP_CAMPANHAS.TABLE_DURATION'),
    align: 'right',
    type: 'mono',
    width: '8%',
  },
];

const formatDuration = seconds => {
  if (!seconds || seconds <= 0) return '—';
  const m = Math.floor(seconds / 60);
  const s = Math.round(seconds % 60);
  if (m > 0) return s > 0 ? `${m}m ${s}s` : `${m}m`;
  return `${s}s`;
};

const tableRows = computed(() =>
  tabela.value.map(c => ({
    title: c.title || '—',
    date: c.created_at
      ? new Date(c.created_at).toLocaleDateString('pt-BR', {
          day: '2-digit',
          month: '2-digit',
        })
      : '—',
    template: c.template || '—',
    team: c.team || '—',
    total: c.total,
    sent: c.sent,
    failed: c.failed,
    rate: `${c.rate}%`,
    duration: formatDuration(c.duration),
  }))
);

const hasDoughnutData = computed(() =>
  Object.values(statusDist.value).some(v => v > 0)
);

// CSV export
const onExportCsv = () => {
  const rows = tabela.value;
  exportCsv(
    rows,
    'Campanha,Data,Template,Equipe,Total,Enviados,Falhas,Taxa%,Duração(s)\n',
    r =>
      `"${r.title || ''}","${r.created_at || ''}","${r.template || ''}","${r.team || ''}",${r.total},${r.sent},${r.failed},${r.rate},${r.duration || 0}`,
    'campanhas-relatorio.csv'
  );
};
</script>

<template>
  <div class="p-6">
    <!-- Header -->
    <div class="mb-4">
      <h1 class="text-xl font-semibold text-n-slate-12">
        {{ t('ISP_REPORTS.ISP_CAMPANHAS.HEADER') }}
      </h1>
      <p class="text-[13px] text-n-slate-10 mt-0.5">
        {{ t('ISP_REPORTS.ISP_CAMPANHAS.SUBTITLE') }}
      </p>
    </div>

    <!-- Filters -->
    <div class="flex items-center gap-3 mb-6 flex-wrap">
      <WootDatePicker
        v-model:date-range="customDateRange"
        v-model:range-type="selectedDateRange"
        @date-range-changed="onDateRangeChange"
      />
      <div v-if="showTeamFilter" class="isp-select-wrap">
        <select
          :value="filters.teamId.value"
          class="isp-select"
          @change="onFilterChange('teamId', $event.target.value)"
        >
          <option value="">
            {{ t('ISP_REPORTS.ISP_CAMPANHAS.FILTER_ALL_TEAMS') }}
          </option>
          <option v-for="team in teams" :key="team.id" :value="team.id">
            {{ team.name }}
          </option>
        </select>
        <span class="isp-select-arrow i-lucide-chevron-down" />
      </div>
      <button class="btn-export ml-auto" @click="onExportCsv">
        <svg
          width="14"
          height="14"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="2"
        >
          <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4" />
          <polyline points="7 10 12 15 17 10" />
          <line x1="12" y1="15" x2="12" y2="3" />
        </svg>
        {{ t('ISP_REPORTS.FILTERS.EXPORT_CSV') }}
      </button>
    </div>

    <!-- Loading -->
    <woot-loading-state
      v-if="campanhas.isFetching && !campanhas.data"
      :message="t('ISP_REPORTS.LOADING')"
      class="h-64"
    />

    <!-- Error -->
    <div
      v-else-if="campanhas.error && !campanhas.data"
      class="flex flex-col items-center justify-center h-64 gap-3"
    >
      <h3 class="text-base font-semibold text-n-slate-12">
        {{ t('ISP_REPORTS.ERROR_TITLE') }}
      </h3>
      <p class="text-sm text-n-slate-10">
        {{ t('ISP_REPORTS.ERROR_MESSAGE') }}
      </p>
      <button
        class="bg-n-brand text-white rounded-md text-sm font-medium px-4 py-2"
        @click="retry"
      >
        {{ t('ISP_REPORTS.RETRY') }}
      </button>
    </div>

    <template v-if="campanhas.data">
      <!-- KPI Cards -->
      <div
        class="grid grid-cols-[repeat(auto-fit,minmax(180px,1fr))] gap-4 mb-6"
      >
        <IspMetricCard
          :label="t('ISP_REPORTS.ISP_CAMPANHAS.KPI_CAMPAIGNS')"
          :value="summary.total_campaigns || 0"
          :trend="makeTrend(summary.total_campaigns_trend)"
          :trend-direction="trendDir(summary.total_campaigns_trend)"
          :loading="campanhas.isFetching"
        />
        <IspMetricCard
          :label="t('ISP_REPORTS.ISP_CAMPANHAS.KPI_SENT')"
          :value="(summary.total_sent || 0).toLocaleString('pt-BR')"
          :trend="makeTrend(summary.total_sent_trend)"
          :trend-direction="trendDir(summary.total_sent_trend)"
          :loading="campanhas.isFetching"
        />
        <IspMetricCard
          :label="t('ISP_REPORTS.ISP_CAMPANHAS.KPI_DELIVERY_RATE')"
          :value="`${summary.delivery_rate || 0}%`"
          :trend="makeTrend(summary.delivery_rate_trend, 'pp')"
          :trend-direction="deliveryTrendDir"
          :loading="campanhas.isFetching"
        />
        <IspMetricCard
          :label="t('ISP_REPORTS.ISP_CAMPANHAS.KPI_FAILURES')"
          :value="(summary.total_failed || 0).toLocaleString('pt-BR')"
          :trend="makeTrend(summary.total_failed_trend)"
          :trend-direction="failedTrendDir"
          :loading="campanhas.isFetching"
          :value-color="summary.total_failed > 0 ? 'text-n-ruby-11' : ''"
        />
      </div>

      <!-- Charts row 1: Evolution + Status Distribution -->
      <div class="charts-grid mb-6">
        <!-- Evolution bar chart -->
        <div v-if="evolucao.length > 1" class="flex flex-col">
          <div class="text-[13px] font-semibold text-n-slate-12 mb-4">
            {{ t('ISP_REPORTS.ISP_CAMPANHAS.CHART_EVOLUTION') }}
          </div>
          <div
            class="bg-n-solid-2 border border-n-weak rounded-[10px] p-5 flex-1"
          >
            <div class="h-[220px]">
              <Bar :data="evolucaoBarData" :options="evolucaoBarOptions" />
            </div>
          </div>
        </div>

        <!-- Status distribution doughnut -->
        <div v-if="hasDoughnutData" class="flex flex-col">
          <div class="text-[13px] font-semibold text-n-slate-12 mb-4">
            {{ t('ISP_REPORTS.ISP_CAMPANHAS.CHART_STATUS') }}
          </div>
          <div
            class="bg-n-solid-2 border border-n-weak rounded-[10px] p-5 flex-1 flex items-center justify-center"
          >
            <div class="h-[220px] w-full">
              <Doughnut :data="doughnutData" :options="doughnutOptions" />
            </div>
          </div>
        </div>
      </div>

      <!-- Charts row 2: Top Errors + Per Team -->
      <div class="charts-grid mb-6">
        <IspBarChart
          v-if="errorItems.length"
          :title="t('ISP_REPORTS.ISP_CAMPANHAS.CHART_ERRORS')"
          :items="errorItems"
          label-width="180px"
        />
        <IspBarChart
          v-if="teamItems.length"
          :title="t('ISP_REPORTS.ISP_CAMPANHAS.CHART_TEAMS')"
          :items="teamItems"
          label-width="130px"
        />
      </div>

      <!-- Comparison Table -->
      <IspDataTable
        v-if="tableRows.length"
        :title="t('ISP_REPORTS.ISP_CAMPANHAS.TABLE_TITLE')"
        :columns="tableColumns"
        :rows="tableRows"
      />
    </template>

    <!-- Empty State -->
    <div
      v-if="!campanhas.isFetching && !campanhas.error && !campanhas.data"
      class="flex flex-col items-center justify-center h-64 gap-2"
    >
      <h3 class="text-base font-semibold text-n-slate-12">
        {{ t('ISP_REPORTS.ISP_CAMPANHAS.EMPTY_TITLE') }}
      </h3>
      <p class="text-sm text-n-slate-10">
        {{ t('ISP_REPORTS.ISP_CAMPANHAS.EMPTY_MESSAGE') }}
      </p>
    </div>
  </div>
</template>

<style scoped>
/* ── Filter select wrapper ───────────────────────────────────────────── */
.isp-select-wrap {
  position: relative;
  display: inline-flex;
  align-items: center;
}
.isp-select-arrow {
  position: absolute;
  right: 9px;
  top: 50%;
  transform: translateY(-50%);
  width: 14px;
  height: 14px;
  pointer-events: none;
  color: #9ca3af;
  flex-shrink: 0;
}

/* ── Filter select: base (light mode) ────────────────────────────────── */
.isp-select {
  appearance: none !important;
  -webkit-appearance: none !important;
  -moz-appearance: none !important;
  width: 100%;
  height: auto;
  margin: 0;
  font-size: 12px;
  font-family: inherit;
  line-height: 1.5;
  padding: 6px 30px 6px 10px;
  border-radius: 8px;
  outline: none;
  cursor: pointer;
  transition:
    border-color 150ms ease,
    background-color 150ms ease;
  background: none;
  background-color: #f8f9fb;
  color: #4b5563;
  border: 1px solid #e0e2e6;
}
.isp-select:hover {
  border-color: #cbd0d6;
}
.isp-select:focus {
  border-color: #2e86c1;
}

/* ── Export button: light mode ───────────────────────────────────────── */
.btn-export {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background: transparent;
  border: 1.5px solid #d1d5db;
  padding: 6px 16px;
  border-radius: 8px;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  transition: all 150ms ease;
  color: #6b7280;
}
.btn-export:hover {
  border-color: #9ca3af;
  color: #374151;
}

/* ── Charts grid: 60/40 layout ──────────────────────────────────────── */
.charts-grid {
  display: grid;
  grid-template-columns: 3fr 2fr;
  gap: 20px;
  align-items: stretch;
}
.charts-grid > :deep(*) {
  display: flex;
  flex-direction: column;
  margin-bottom: 0;
}

/* ── DatePicker z-index fix ──────────────────────────────────────────── */
:deep(.date-picker) {
  z-index: 20;
}

/* ── Responsive ──────────────────────────────────────────────────────── */
@media (max-width: 768px) {
  .charts-grid {
    grid-template-columns: 1fr;
  }
}
</style>

<!-- Dark mode overrides (unscoped for reliable body.dark targeting) -->
<style>
body.dark .isp-select {
  background-color: transparent;
  color: rgba(255, 255, 255, 0.72);
  border-color: rgba(255, 255, 255, 0.08);
}
body.dark .isp-select:hover {
  border-color: rgba(255, 255, 255, 0.14);
  background-color: rgba(255, 255, 255, 0.03);
}
body.dark .isp-select-arrow {
  color: #64748b;
}
body.dark .btn-export {
  border-color: rgba(255, 255, 255, 0.1);
  color: rgba(255, 255, 255, 0.6);
}
body.dark .btn-export:hover {
  border-color: rgba(255, 255, 255, 0.2);
  color: rgba(255, 255, 255, 0.85);
}
</style>
